import React, { useState, useEffect, useMemo } from "react";
import { useFormikContext } from "formik";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ConfirmDeleteModal from "../../images/ConfirmDeleteModal";
import useOrderedFields from "../../../hooks/useOrderedFields";
import { FIELD_ORDER_SECTIONS } from "../../../utils/fieldOrder";
import FieldReorderList from "../../capture/FieldReorderList";
import useAuth from "../../../hooks/useAuth";

export default function FieldOrderSettings() {
  const { values, setFieldValue } = useFormikContext();
  const { user } = useAuth();
  const { settings } = user.company;
  const computedOrderedFields = useOrderedFields(
    values.fieldSettings,
    values.fieldSettings?.fieldOrder
  );
  const computedOrderedFieldsSignature = useMemo(
    () =>
      JSON.stringify(
        Object.fromEntries(
          Object.entries(computedOrderedFields).map(([section, fields]) => [
            section,
            fields.map(field => field.id),
          ])
        )
      ),
    [computedOrderedFields]
  );

  const [orderedFields, setOrderedFields] = useState({});
  const [resetConfirmationOpen, setResetConfirmationOpen] = useState(false);

  const visibleSections = useMemo(() => {
    return FIELD_ORDER_SECTIONS.filter(section => {
      if (section.id === "configurable" && !settings.user_configurable_custom_fields) {
        return false;
      }
      if (section.id === "storage" && !settings.inventory_tracking) {
        return false;
      }
      return (orderedFields[section.id] || []).length > 0;
    });
  }, [
    orderedFields,
    settings.user_configurable_custom_fields,
    settings.inventory_tracking,
  ]);

  useEffect(() => {
    setOrderedFields(computedOrderedFields);
  }, [computedOrderedFieldsSignature]);

  const updateFieldOrder = newOrderedFields => {
    setOrderedFields(newOrderedFields);

    const sectionsData = {};
    visibleSections.forEach(section => {
      const sectionFields = newOrderedFields[section.id] || [];
      sectionsData[section.id] = sectionFields.map(f => f.id);
    });

    const fieldOrder = {
      version: 1,
      sections: sectionsData,
    };

    setFieldValue("fieldSettings.fieldOrder", fieldOrder);
  };

  const handleResetToDefault = () => {
    setResetConfirmationOpen(true);
  };

  const confirmReset = () => {
    setFieldValue("fieldSettings.fieldOrder", null);
    setResetConfirmationOpen(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 2,
        height: "100%",
        width: "100%",
      }}
    >
      <Box display="flex" justifyContent="flex-end" alignItems="center" flexShrink={0}>
        <Button variant="text" color="error" onClick={handleResetToDefault}>
          Reset to Default
        </Button>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
        <FieldReorderList
          orderedFields={orderedFields}
          onOrderChange={updateFieldOrder}
          sections={visibleSections}
        />
      </Box>

      {resetConfirmationOpen && (
        <ConfirmDeleteModal
          title="Reset to Default"
          descr="Are you sure you want to reset the fields order to default?"
          onClose={() => setResetConfirmationOpen(false)}
          onDelete={confirmReset}
          applyLabel="Reset"
        />
      )}
    </Box>
  );
}
